'use client';

import {
  Button,
  ButtonGroup,
  Divider,
  Icon,
  InputField,
  Spinner,
  Text,
  Tooltip,
} from '@stone-payments/jade';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePaymentMethod } from '@payments-checkout/shared/providers';

import { useSendBillingAddress } from '../../../hooks/useSendBillingAddress';
import { useSendShippingAddress } from '../../../hooks/useSendShippingAddress';
import { useCreditCardPayment } from '../../../hooks/useCreditCardPayment';

import { yupResolver } from '@hookform/resolvers/yup';
import { isDefaultBrand } from '@payments-checkout/shared/helpers/constants/brand';
import { isInternalServerError } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { billingInformationSchema } from '@payments-checkout/shared/helpers/constants/validation-input';
import { errorMessages } from '@payments-checkout/shared/helpers/constants/input-error-messages';
import { StringManager } from '@payments-checkout/shared/helpers/index';
import {
  reportError,
  setObservabilityContext,
} from '@payments-checkout/shared/observability';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { DigitalWalletsType } from '@payments-checkout/shared/plugins/analitica/analitica.types';
import {
  FormProvider,
  Resolver,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { InstallmentsForm } from '../../../app/components/installmentsForm/installmentsForm';
import { RecurrenceAgreement } from '../../../app/components/recurrence/recurrenceAgreement';
import { useFormTypedValues } from '../../../app/providers/formValuesProvider.provider';
import { BillingInformationFormFields } from '../../../app/types/billingInformation.types';
import { ApplePayPaymentButton } from '../../../applePay/components/applePayPaymentButton.component';
import { ClickToPayWrapper } from '../../../clickToPay/components/clickToPayWrapper.component';
import { useClickToPay } from '../../../clickToPay/hooks/useClickToPayWalletData.hook';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { usePageSharedData } from '../../../hooks/usePageSharedData/usePageSharedData';
import { PaymentFailedAlert } from '../../alert';
import { Address } from '../../forms/address';
import { CreditCardForm } from '../../forms/creditCardForm';
import { adapterAddressFields, isCreditCard } from './creditCardAddress.helpers';
import { AddressFields } from '../../../types/address';
import {
  errorMessageGeneric,
  errorMessagePaymentGeneric,
} from './errorMessage';

const FIELD_REFERENCES: Record<string, string> = {
  'billing-address-zip-code': 'campo_cep',
  'billing-address-city': 'campo_cidade',
  'billing-address-street': 'campo_endereco',
  'billing-address-district': 'campo_bairro',
  'billing-address-number': 'campo_numero',
  'billing-address-complement': 'campo_complemento',
  'billing-address-country': 'select_pais',
  'billing-address-state': 'select_estado',
  creditCard: 'campo_numero_cartao',
  cardHolder: 'campo_nome_cartao',
  expirationDate: 'campo_vencimento_cartao',
  cvv: 'campo_cvv_cartao',
  installments: 'select_parcelas',
};

declare global {
  interface Window {
    onSubmit: any;
    hst_checkout: () => void;
  }
}

export function CardCreditCardAddress() {
  const {
    paymentMethod,
    paymentWithClickToPay,
    selectedDigitalWallet,
    setFailedPaymentMessage,
    setShowFailedPaymentAlert,
    brand,
    isBrandReady,
    setIsCustomerFormLoading,
    setShowClickToPay,
    submitDisabled,
    installments,
    clickToPayCardType,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText,
    saveCardForFutureChecked,
  } = usePaymentMethod();

  const { initializeClickToPay, isClickToPayPaymentPending, onHstSrcCancel } =
    useClickToPay();

  const { formatMessage } = useIntl();
  const { back } = useRouter();
  const { paymentLinkIdData, isRecurrence, isDeliveryEnabled, showAddressPage } =
    usePageSharedData();

  const acceptsCredit =
    !!paymentLinkIdData.data?.payment_settings?.accepted_payment_methods?.includes(
      'credit_card'
    );
  const isC2PDebit =
    selectedDigitalWallet === 'clickToPay' &&
    (clickToPayCardType === 'DEBIT' || !acceptsCredit);
  const isC2PInstallmentsMissing =
    paymentWithClickToPay && !installments && !isC2PDebit;
  const { formValues, setFormValues } = useFormTypedValues();
  const smallViewSize = useBreakpoint(500);
  const [errorMessage, setErrorMessage] = useState('');
  // Populado em `onSubmit` antes de `onSendBillingAddressSuccess` poder rodar.
  const [address, setAddress] = useState({} as { address: AddressFields });

  const apiBillingAddress =
    paymentLinkIdData.data?.customer_settings?.customer?.address;

  const defaultBillingAddress = useMemo(() => {
    if (!apiBillingAddress) return undefined;

    let parsedAddress = { ...apiBillingAddress };

    if (apiBillingAddress.line1) {
      const parts = apiBillingAddress.line1.split(',').map((s) => s.trim());
      const [first, second, third] = parts;

      parsedAddress = {
        ...parsedAddress,
        ...(parts.length === 3
          ? { number: first, street: second, neighborhood: third }
          : { street: first, neighborhood: second }),
      };
    }

    if (apiBillingAddress.line2) {
      parsedAddress.complement = apiBillingAddress.line2.trim();
    }

    return parsedAddress;
  }, [apiBillingAddress]);

  const handleBack = () => {
    analiticaInstance.events.billingPage.backClicked();
    setInstallments(null);
    setInstallmentsExplanationText(null);
    setSurchargesText(null);
    back();
  };

  const onInvalid: SubmitErrorHandler<BillingInformationFormFields> = (
    formErrors
  ) => {
    Object.entries(formErrors).forEach(([field, error]) => {
      const reference = FIELD_REFERENCES[field];
      if (!reference) return;

      analiticaInstance.events.billingPage.fieldError(
        reference,
        (error as { message?: string })?.message ?? ''
      );
    });
  };

  // No contexto global, e não por chamada: vale também para os erros de 3DS e de
  // endereço, que acontecem depois de a bandeira já ser conhecida.
  useEffect(() => {
    if (!isBrandReady || isDefaultBrand(brand)) return;
    setObservabilityContext({ card_brand: brand });
  }, [brand, isBrandReady]);

  useEffect(() => {
    setIsCustomerFormLoading(false);
    analiticaInstance.events.billingPage.pageViewed(
      saveCardForFutureChecked ?? undefined
    );
    if (paymentWithClickToPay) {
      initializeClickToPay();
      setShowClickToPay(true);
    }
  }, []);

  const onSubmit: SubmitHandler<BillingInformationFormFields> = (
    data: BillingInformationFormFields
  ) => {
    analiticaInstance.events.billingPage.finishClicked(
      String(methods.getValues('installments') ?? ''),
      isDefaultBrand(brand) ? '' : brand,
      saveCardForFutureChecked ?? undefined
    );
    setShowFailedPaymentAlert(false);
    const billingAddress = adapterAddressFields(data);
    setAddress(billingAddress);
    sendBillingAddress({ address: billingAddress.address });
    setFormValues((prev) => ({ ...prev, ...methods.getValues() }));
  };

  async function onSendBillingAddressSuccess() {
    if (paymentWithClickToPay) {
      if (!installments && !isC2PDebit) {
        setShowFailedPaymentAlert(true);
        setFailedPaymentMessage(
          formatMessage({ id: 'creditCardForm.installments.required' }) ||
            'Por favor, selecione o número de parcelas.'
        );
        return;
      }
      window.hst_checkout();
    } else {
      creditCardPayment.submitPayment(address);
    }
    sendEvent();
  }

  function sendEvent() {
    if (selectedDigitalWallet) {
      const digitalWalletPossibleValues = {
        applePay: 'Apple Pay',
        googlePay: 'Google Pay',
        clickToPay: 'Click to pay',
      };
      analiticaInstance.events.paymentPage.digitalWalletsFinishClick(
        digitalWalletPossibleValues[selectedDigitalWallet] as DigitalWalletsType
      );
    }
  }

  const validationSchema = billingInformationSchema({
    isCreditCard: isCreditCard(paymentMethod) && !paymentWithClickToPay,
    isRecurrencePayment: isRecurrence,
    brand,
  });

  const methods = useForm<BillingInformationFormFields>({
    defaultValues: {
      'billing-address-street':
        formValues?.['billing-address-street'] || defaultBillingAddress?.street,
      'billing-address-number':
        formValues?.['billing-address-number'] || defaultBillingAddress?.number,
      'billing-address-complement':
        formValues?.['billing-address-complement'] ||
        defaultBillingAddress?.complement,
      'billing-address-district':
        formValues?.['billing-address-district'] ||
        defaultBillingAddress?.neighborhood,
      'billing-address-city':
        formValues?.['billing-address-city'] || defaultBillingAddress?.city,
      'billing-address-state':
        formValues?.['billing-address-state'] || defaultBillingAddress?.state,
      'billing-address-zip-code': StringManager.formatBrazilianCeP(
        formValues?.['billing-address-zip-code'] ||
          defaultBillingAddress?.zip_code
      ),
      'billing-address-country':
        formValues?.['billing-address-country'] ||
        defaultBillingAddress?.country ||
        'BR',
      'shipping-address-country':
        formValues?.['shipping-address-country'] ||
        defaultBillingAddress?.country ||
        'BR',
      ...(isDeliveryEnabled || showAddressPage
        ? {
            'shipping-address-street':
              formValues?.['shipping-address-street'] ?? '',
            'shipping-address-number':
              formValues?.['shipping-address-number'] ?? '',
            'shipping-address-complement':
              formValues?.['shipping-address-complement'] ?? '',
            'shipping-address-district':
              formValues?.['shipping-address-district'] ?? '',
            'shipping-address-city':
              formValues?.['shipping-address-city'] ?? '',
            'shipping-address-state':
              formValues?.['shipping-address-state'] ?? '',
            'shipping-address-zip-code':
              formValues?.['shipping-address-zip-code'] ?? '',
            'shipping-address-without-number':
              formValues?.['shipping-address-without-number'] ?? false,
          }
        : {}),
      'allow-recurrence': false,
    },
    resolver: yupResolver(
      validationSchema
    ) as unknown as Resolver<BillingInformationFormFields>,
    mode: 'onTouched',
  });

  const creditCardPayment = useCreditCardPayment({
    getValues: methods.getValues,
  });

  const { mutate: sendBillingAddress, isPending: isSendBillingPending } =
    useSendBillingAddress({
      onError: (error) => {
        const errorStatus = error?.response?.status ?? 500;
        reportError(error, { source: 'billing_address' });

        setErrorMessage(
          isInternalServerError(errorStatus)
            ? errorMessageGeneric(1004)
            : errorMessagePaymentGeneric(1000)
        );
      },
      onSuccess: () => onSendBillingAddressSuccess(),
    });

  const { mutate: sendShippingAddress, isPending: isSendShippingPending } =
    useSendShippingAddress();

  const hasInstallmentsInDigitalWallet =
    selectedDigitalWallet === 'applePay' ||
    (selectedDigitalWallet === 'clickToPay' && !isC2PDebit);

  const isFormDataLoading =
    isSendBillingPending ||
    isSendShippingPending ||
    creditCardPayment.isPaymentPending ||
    creditCardPayment.isTokenizeCardPending ||
    isClickToPayPaymentPending ||
    creditCardPayment.tdsLoading ||
    !isBrandReady;

  return (
    <div className="w-full flex">
      <div className="flex flex-col grow px-jade-200 gap-jade-150 mx-auto mt-jade-200 mb-jade-300 max-w-[416px] md:mt-jade-600">
        <FormProvider {...methods}>
          <div id="tdsMethodContainer" style={{ display: 'none' }} />
          <form
            onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
            data-pagarmecheckout-form
          >
            <div style={{ display: 'none' }}>
              <InputField
                type="text"
                value={creditCardPayment.tdsToken}
                label="tdsToken"
              />
              <InputField
                type="text"
                data-pagarmecheckout-element="number"
                value={methods.getValues().creditCard || ''}
                label="creditCardNumber"
              />
            </div>
            <Address
              isStoneDelivery={isDeliveryEnabled}
              showShippingAddress={showAddressPage}
            />
            {isCreditCard(paymentMethod) && !paymentWithClickToPay && (
              <div className="w-full flex items-center flex-col">
                <CreditCardForm disabled={isFormDataLoading} />
                {isRecurrence && <RecurrenceAgreement />}
              </div>
            )}
            {paymentWithClickToPay && (
              <>
                <ClickToPayWrapper onCloseClick={onHstSrcCancel} />
                <div className="block w-full py-2 pt-jade-400">
                  <Divider direction="horizontal" size="small" />
                </div>
              </>
            )}
            {hasInstallmentsInDigitalWallet && (
              <div className="w-full flex items-center flex-col">
                <InstallmentsForm />
              </div>
            )}
            <div className="flex justify-center items-center w-full gap-jade-150 mt-jade-300 mb-jade-300 flex-col">
              <PaymentFailedAlert />
              {isFormDataLoading && (
                <Spinner
                  size="large"
                  color="brand"
                  data-testid="loading-payment-request"
                />
              )}
            </div>
            <div className="w-full flex flex-col-reverse items-center justify-between mt-5 gap-jade-300 sm:flex-row">
              <div className="flex items-center gap-jade-100">
                <Icon color="medium" size="small" use="lock-close" />
                <Text
                  color="medium"
                  variant="text-medium"
                  weight="regular"
                  className="ml-jade-50 mr-jade-50 whitespace-nowrap"
                >
                  {formatMessage({ id: 'protectedPayment' })}
                </Text>
                <Tooltip
                  placement="top"
                  trigger={
                    <Icon color="medium" size="small" use="circle-info" />
                  }
                  text={formatMessage({ id: 'footer.tooltip' })}
                />
              </div>
              {selectedDigitalWallet === 'applePay' ? (
                <div className="flex flex-row gap-jade-100">
                  <Button size="medium" onClick={handleBack}>
                    {formatMessage({ id: 'button.action.back' })}
                  </Button>
                  <ApplePayPaymentButton />
                </div>
              ) : (
                <div className="w-full md:w-2/5 ">
                  <ButtonGroup
                    expanded={!!smallViewSize}
                    direction={smallViewSize ? 'vertical' : 'horizontal'}
                    items={[
                      {
                        id: 'cart-page-checkout-button',
                        text: formatMessage({ id: 'button.action.pay' }),
                        variant: 'primary-solid',
                        type: 'submit',
                        loading: isFormDataLoading,
                        disabled: submitDisabled || isC2PInstallmentsMissing,
                      },
                      {
                        text: formatMessage({ id: 'button.action.back' }),
                        onClick: () => {
                          handleBack();
                          setFormValues((prev) => ({
                            ...prev,
                            ...methods.getValues(),
                          }));
                        },
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
